import { supabase } from './supabase';

const AUTH_TIMEOUT = 30000; // 30 seconds timeout

export const handleAuthRequest = async <T>(
  authPromise: Promise<T>,
  options: { retries?: number; retryDelay?: number } = {}
): Promise<T> => {
  const { retries = 2, retryDelay = 1000 } = options;
  let attempt = 0;

  const executeWithTimeout = async (): Promise<T> => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), AUTH_TIMEOUT);

    try {
      const result = await Promise.race([
        authPromise,
        new Promise((_, reject) => {
          controller.signal.addEventListener('abort', () => {
            reject(new Error('Authentication request timed out'));
          });
        })
      ]) as T;

      clearTimeout(timeoutId);
      return result;
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      if (error.message === 'Authentication request timed out' && attempt < retries) {
        attempt++;
        await new Promise(resolve => setTimeout(resolve, retryDelay * attempt));
        return executeWithTimeout();
      }
      
      throw error;
    }
  };

  return executeWithTimeout();
};

export const checkAuthStatus = async () => {
  try {
    const { data: { session }, error: sessionError } = await handleAuthRequest(
      supabase.auth.getSession()
    );

    if (sessionError) {
      console.error('Session error:', sessionError);
      return { user: null, error: sessionError };
    }

    if (!session) {
      return { user: null, error: null };
    }

    // Try to refresh the session if it's close to expiring
    if (session.expires_at) {
      const expiresAt = new Date(session.expires_at * 1000);
      const now = new Date();
      const timeUntilExpiry = expiresAt.getTime() - now.getTime();
      
      // If session expires in less than 5 minutes, refresh it
      if (timeUntilExpiry < 300000) {
        const { data: { session: refreshedSession }, error: refreshError } = 
          await handleAuthRequest(supabase.auth.refreshSession());
        
        if (refreshError) {
          console.error('Session refresh error:', refreshError);
          return { user: null, error: refreshError };
        }
        
        return { user: refreshedSession?.user || null, error: null };
      }
    }

    return { user: session.user, error: null };
  } catch (error) {
    console.error('Auth status check error:', error);
    return { user: null, error };
  }
};

export const signIn = async (email: string, password: string) => {
  return handleAuthRequest(
    supabase.auth.signInWithPassword({
      email: email.trim(),
      password: password.trim()
    })
  );
};

export const signUp = async (email: string, password: string) => {
  return handleAuthRequest(
    supabase.auth.signUp({
      email: email.trim(),
      password: password.trim(),
      options: {
        emailRedirectTo: `${window.location.origin}`,
        data: {
          username: email.split('@')[0],
        }
      }
    })
  );
};

export const resetPassword = async (email: string) => {
  return handleAuthRequest(
    supabase.auth.resetPasswordForEmail(email.trim(), {
      redirectTo: `${window.location.origin}/reset-password`,
    })
  );
};

export const signOut = async () => {
  return handleAuthRequest(supabase.auth.signOut());
};

export const getSession = async () => {
  return handleAuthRequest(supabase.auth.getSession());
};

export const refreshSession = async () => {
  return handleAuthRequest(supabase.auth.refreshSession());
};